import React from 'react';
import { Link } from 'react-router-dom';
import { FiPhone, FiArrowRight } from 'react-icons/fi';
import LocationVehiclePage from './LocationVehiclePage';

const seoConfig = {
  city: 'Wise County',
  state: 'TX',
  slug: 'wise-county',
  county: 'Wise County',
  title: 'Used Cars Wise County TX - Local Dealer in Paradise',
  h1: 'Wise County\'s Used Car Dealer — Born and Based in Paradise',
  metaDescription: 'Wise County\'s only used car dealer with a full service shop. Located in Paradise TX. Trucks, cars, SUVs. Financing for all credit. Open Mon-Sat. (817) 382-2316.',
  ogTitle: 'Used Cars in Wise County - Paradise-Performance',
  ogDescription: 'We don\'t claim to serve Wise County from 50 miles away. We live here. Vehicles — and a repair shop that handles everything.',
  ctaHeadline: 'We\'re in the Center of Wise County',
  ctaSubline: 'Paradise sits right in the middle. Wherever you live in the county, we\'re a short drive.',
  faqs: [
    {
      q: 'Where are you located in Wise County?',
      a: 'Paradise, TX — 305 Co Rd 3470. Right in the center of the county. Decatur is 10 minutes north, Bridgeport 6 minutes west, Boyd 15 minutes south.'
    },
    {
      q: 'What makes you different from other Wise County car lots?',
      a: 'We\'re the only dealer in the county with a full repair shop on the same property. We sell vehicles and we fix them. European car specialist, diesel work, general maintenance — all here.'
    },
    {
      q: 'What vehicles do you carry?',
      a: 'Used trucks, SUVs, sedans and European cars (BMW, Audi, VW). Inventory changes weekly. Most vehicles priced under $20K.'
    },
    {
      q: 'Do you finance buyers with bad credit?',
      a: 'We finance every type of credit. Bad credit, no credit, first-time buyers, self-employed, seasonal income. We use lenders who understand rural Texas. Most approvals are same day.'
    }
  ]
};

const WiseCounty = () => (
  <LocationVehiclePage config={seoConfig}>

    {/* ─── Hero ─── */}
    <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(217,119,6,0.15) 0%, transparent 50%)'
        }} />
      </div>
      <div className="relative container mx-auto px-4">
        <div className="max-w-4xl">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Wise County's Used Car Dealer — Born and Based in Paradise
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-300 max-w-3xl">
            Not a Fort Worth lot claiming to serve Wise County from 50 miles out. We live here. We sell here. We fix what we sell.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/inventory"
              className="inline-flex items-center justify-center px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold text-lg transition-colors"
            >
              View Inventory <FiArrowRight className="ml-2" />
            </Link>
            <a
              href="tel:+18173822316"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 border border-white/30 text-white rounded-lg font-semibold text-lg hover:bg-white/20 transition-colors"
            >
              <FiPhone className="mr-2" /> (817) 382-2316
            </a>
          </div>
        </div>
      </div>
    </section>

    {/* ─── Why Wise County Stays Local ─── */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-amber-600 font-semibold mb-2">Wise County, TX</p>
          <h2 className="text-3xl font-bold mb-6">922 Square Miles of Ranch Land. One Real Dealership.</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed text-lg">
            <p>Wise County stretches from Lake Bridgeport in the northwest to Boyd in the southeast. Ranches, small towns, FM roads and people who don't have time for games. We sit right in the middle of it in Paradise.</p>
            <p>Every time someone from Wise County drives to Fort Worth or Denton for a vehicle, they burn two hours in traffic. They pay metro markups. They deal with salespeople who've never driven a county road and don't know what a ranch gate clearance looks like.</p>
            <p>We know this county because we're from here. We know 287 gets backed up during Decatur ISD drop-off. We know FM 1655 floods after heavy rain. We know which trucks hold up on caliche roads and which ones shake apart after a year. Our inventory reflects that.</p>
          </div>
        </div>
      </div>
    </section>

    {/* ─── What Rural Buyers Actually Need ─── */}
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-amber-600 font-semibold mb-2">Rural Texas</p>
          <h2 className="text-3xl font-bold mb-6">Vehicles for the Way Wise County Actually Lives</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-3">Trucks That Work</h3>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p>Half the "trucks" on metro lots are $55K lifestyle vehicles with leather seats and chrome bed rails that have never seen a hay bale. We carry trucks that tow, haul and handle dirt roads without falling apart.</p>
                <p>Half-tons for daily use. Three-quarter-tons for the serious stuff. Used, inspected and priced for people who need a truck — not people shopping for a status symbol.</p>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-3">Daily Drivers & Commuter Cars</h3>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p>Plenty of Wise County folks commute into DFW for work. Down 287, down 114, over 380. You need a reliable vehicle that sips gas and doesn't strand you on Highway 81 during rush hour.</p>
                <p>We carry fuel-efficient sedans, crossovers and compacts for exactly this. Most under $15K. All inspected and warranted.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ─── Every Town We Serve ─── */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <p className="text-amber-600 font-semibold mb-2 text-center">Towns We Serve</p>
          <h2 className="text-3xl font-bold text-center mb-4">We've Sold Vehicles to Every Corner of Wise County</h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">From ranchers in Alvord to retirees in Runaway Bay. Young families in Boyd to contractors in Decatur. This is our backyard.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/used-cars-decatur-tx" className="block bg-gray-50 rounded-xl p-6 hover:shadow-md transition-shadow">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Decatur</h3>
              <p className="text-gray-600">County seat. 10 minutes north. The courthouse crowd, the 287 commuters, the ISD families — they all come to us because the prices beat anything on the Decatur strip.</p>
            </Link>
            <Link to="/used-cars-bridgeport-tx" className="block bg-gray-50 rounded-xl p-6 hover:shadow-md transition-shadow">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Bridgeport</h3>
              <p className="text-gray-600">Lake town. 6 minutes west. If you own lake property, you need a tow rig. We sell, service and finance.</p>
            </Link>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Boyd</h3>
              <p className="text-gray-600">Southeast corner of the county. Growing fast with the DFW sprawl pushing north. Young families wanting affordable trucks and SUVs without metro prices.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Alvord</h3>
              <p className="text-gray-600">North Wise County ranch country. Working trucks and utility vehicles. People out here need dependable machines, not flashy lots.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Rhome & Newark</h3>
              <p className="text-gray-600">Southern Wise County on the DFW border. Commuter territory. Need vehicles that handle daily highway miles and weekend projects.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Chico & Runaway Bay</h3>
              <p className="text-gray-600">Northwest lake and ranch area. Boats, equipment — all of it needs to be stored, moved and maintained. We handle the vehicle side.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ─── The Shop ─── */}
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">The Only Dealer + Shop Combo in Wise County</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed text-lg">
            <p>Lots of places sell used cars. Very few can fix them when something goes wrong. We do both. Same property. Same team. Buy a truck here on Tuesday. Bring it back for an oil change next month. Check engine light? Drive it right over.</p>
            <p>Our <Link to="/services" className="text-amber-600 hover:underline">repair shop</Link> handles everything from routine maintenance to major engine work. <Link to="/services/european-car-repair" className="text-amber-600 hover:underline">European car diagnostics</Link> — BMW, Audi, Mercedes, VW. <Link to="/services/dallas-diesel-paradise-pros" className="text-amber-600 hover:underline">Diesel trucks</Link> — Powerstroke, Duramax, Cummins. General service on anything with an engine.</p>
            <p>Every vehicle we sell comes with a warranty. That warranty gets serviced right here. No "call the 800 number." No "find an authorized facility." Just bring it back to the people who sold it to you.</p>
          </div>
        </div>
      </div>
    </section>

    {/* ─── Financing ─── */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">Financing for How Wise County Earns a Living</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed text-lg">
            <p>Ranchers with seasonal income. Contractors on 1099s. Young people buying their first vehicle out of Decatur High or Chico ISD. Retirees on fixed income. We've financed every scenario.</p>
            <p>Our lenders understand rural Texas. They don't see Wise County as a risk category. They see it as a community where people pay their bills.</p>
          </div>
          <div className="mt-8">
            <Link
              to="/finance-application"
              className="inline-flex items-center px-8 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors"
            >
              Apply for Financing <FiArrowRight className="ml-2" />
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* ─── Beyond Wise County ─── */}
    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Beyond the County Line</h2>
          <p className="text-center text-gray-600 mb-6">We serve buyers from Parker County to Denton County. If you can get to Paradise, we've got a vehicle for you.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <Link to="/used-cars-weatherford-tx" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Weatherford / Parker County</h3>
              <p className="text-gray-500 text-sm">30 min from I-20. Skip the strip-mall markup.</p>
            </Link>
            <Link to="/used-cars-denton-tx" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Denton / Denton County</h3>
              <p className="text-gray-500 text-sm">35 min on 380. European cars and student vehicles.</p>
            </Link>
          </div>
        </div>
      </div>
    </section>

  </LocationVehiclePage>
);

export default WiseCounty;
